import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'core/components';
import { RequestReturnType } from 'core/enums';
import { useAgGridServerTableContext, useNotificationContext } from 'core/states';
import { QuoteEntityType, QuoteStatus, QuoteType, UpsertQuoteAction } from 'quotes/enums';
import { useQuoteActions } from 'quotes/hooks';
import { IBaseQuote } from 'quotes/types';
import { Color, Variant } from 'theme/enums';

import { ArchiveConfirmation } from './ArchiveConfirmation';

interface IProps {
  entityType: QuoteEntityType;
  quoteType: QuoteType;
}

export const ArchiveQuoteButton = ({ entityType, quoteType }: IProps) => {
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  const { t } = useTranslation();

  const { isLoadingTopNotificationDisplayed } = useNotificationContext();
  const { selectedRows, deselectAllRows, gridApi, updateRows } = useAgGridServerTableContext();

  const isArchivedQuoteSelected = selectedRows.some((quote: IBaseQuote) => quote.archived);

  const isActiveQuoteSelected = selectedRows.some((quote: IBaseQuote) => !quote.archived);

  const allSelectedQuotesAreArchivable =
    QuoteType.CUSTOMER === quoteType ||
    selectedRows.every((quote: IBaseQuote) => QuoteStatus.EDITABLE_BY_CUSTOMER === quote.status);

  const { upsertCustomerQuotes, upsertVendorQuotes } = useQuoteActions({
    entityType,
    action: isActiveQuoteSelected ? UpsertQuoteAction.ARCHIVE : UpsertQuoteAction.RESTORE,
  });

  const onSubmit = async () => {
    setIsConfirmationOpen(false);

    const upsertQuotesAction =
      quoteType === QuoteType.CUSTOMER ? upsertCustomerQuotes : upsertVendorQuotes;

    const { success, quotes: updatedQuotes } = await upsertQuotesAction({
      quotes: selectedRows?.map((quote: IBaseQuote) => {
        return {
          id: quote.id,
          archived: isActiveQuoteSelected ? 1 : 0,
        };
      }),
      returnType: RequestReturnType.FULL_RECORD_AS_ARRAY,
    });

    if (!success) {
      return;
    }

    handleOnSuccess(updatedQuotes);
  };

  const handleOnSuccess = (quotes: IBaseQuote[]) => {
    deselectAllRows();

    const archivedFilterData = gridApi.getFilterModel()['archived'];

    if (!archivedFilterData || archivedFilterData?.values.length === 0) {
      updateRows({ update: quotes });

      return;
    }

    updateRows({ remove: quotes });
  };

  const buttonLabelKey = isActiveQuoteSelected ? 'buttonLabelArchive' : 'buttonLabelRestore';

  return (
    <>
      <Button
        text={t(buttonLabelKey)}
        color={Color.NEUTRAL}
        variant={Variant.OUTLINED}
        onClick={() => setIsConfirmationOpen(true)}
        disabled={
          isLoadingTopNotificationDisplayed ||
          (isArchivedQuoteSelected && isActiveQuoteSelected) ||
          !allSelectedQuotesAreArchivable
        }
      />

      <ArchiveConfirmation
        isOpen={isConfirmationOpen}
        onConfirm={onSubmit}
        onCancel={() => setIsConfirmationOpen(false)}
        entityType={entityType}
        archive={isActiveQuoteSelected}
      />
    </>
  );
};
